import type { CommunicationParticipantDto } from "@/api/communications";
import { cn } from "@/design-system/lib/utils";

interface Props {
  participant: CommunicationParticipantDto;
  className?: string;
}

/**
 * Lightweight chip for a CommunicationParticipant. Shows display name
 * and (when present) the role hint. Phone/email is exposed via title
 * tooltip rather than visible text — keeps the chip compact while still
 * letting compliance reviewers verify the contact channel.
 */
export function ParticipantChip({ participant, className }: Props) {
  const tooltip =
    participant.phoneOrEmail ?? participant.displayName;
  return (
    <span
      title={tooltip}
      className={cn(
        "inline-flex items-center gap-space-2 px-space-2 py-[3px] rounded-md",
        "border border-border-light bg-white-pure text-small text-onyx",
        className,
      )}
    >
      <span className="truncate max-w-[10rem]">{participant.displayName}</span>
      {participant.roleHint && (
        <span className="font-display text-[9px] font-bold uppercase tracking-[0.1em] text-gold-shadow">
          {participant.roleHint}
        </span>
      )}
    </span>
  );
}
